import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { useAgents } from '../../hooks/agents/useAgents'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { KnowledgeSource, MessageThread, UploadedAsset } from '../../types/chatTypes'
import { ParsedCommand } from '../../types/commandTypes'
import { AIModel } from '../../types/modelTypes'
import ChatBubble from '../ChatBubble'
import { QuestionInput } from '../QuestionInput'
import { AgentWatermark } from './AgentWatermark'

const CHAT_SPACING = {
  horizontal: [4, 4, 16, 32, 48, 64],
}

// Pixels of empty space left above the freshly-sent message when it anchors to
// the top of the scroll viewport, so it doesn't sit flush under the chat header.
// Applied by stopping the scroll short rather than padding the thread: the
// message already carries a top margin that collapses to just above the anchor,
// so stopping short reveals a clean slice of that empty margin. Padding the
// thread instead would break the margin collapse and jump the gap by the full
// margin (~20px) the moment it's non-zero, leaving no fine control.
const SCROLL_ANCHOR_TOP_GAP = 12

interface ChatInterfaceProps {
  conversation: MessageThread[]
  chatContainerRef: React.RefObject<HTMLDivElement>
  isInputDisabled: boolean
  isTyping: boolean // To pass to QuestionInput or for other UI elements if needed
  onSend: (message: string, attachedDocument?: UploadedAsset, commands?: ParsedCommand[]) => void
  onRetry: (question: string, tempId: string, searchKnowledgeSource?: KnowledgeSource) => void
  onTypingChange: (isTyping: boolean) => void
  hasDocumentsInConversation: boolean // Prop to indicate if conversation contains documents
  onToggleResponseMinimized?: (threadTempId: string, responseMessageId: string) => void
  currentPhase?: { tempId: string; phase: 'internal_streaming' | 'llm_resumed' } | null
  isStreaming?: boolean // Track if any response is currently streaming
  onStop?: () => void // Abort the in-flight response (Stop button)
  models?: AIModel[]
  selectedModelId?: string
  onSelectModel?: (modelId: string | undefined) => void
  conversationId?: string
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  conversation,
  chatContainerRef,
  isInputDisabled,
  onSend,
  onRetry,
  onTypingChange,
  hasDocumentsInConversation,
  onToggleResponseMinimized,
  currentPhase,
  isStreaming,
  onStop,
  models,
  selectedModelId,
  onSelectModel,
  conversationId,
}) => {
  const { t } = useLanguage()
  const { currentAgent } = useAgents()

  // Color mode values
  const textMutedColor = useColorModeValue('gray.500', 'gray.400')
  const inputContainerBg = useColorModeValue('white', 'gray.800')

  // Attached to the most recent thread so a freshly submitted question can be
  // scrolled to the top of the viewport.
  const lastThreadRef = useRef<HTMLDivElement | null>(null)
  // Armed on send/retry, consumed once when the conversation actually grows.
  const shouldScrollOnGrowthRef = useRef(false)
  const previousConversationLengthRef = useRef(conversation.length)
  // Viewport height of the scroll container, used to reserve a full screen for
  // the active thread so its question can reach the top.
  const [viewportHeight, setViewportHeight] = useState(0)

  useEffect(() => {
    const container = chatContainerRef.current
    if (!container) return

    const updateViewportHeight = () => setViewportHeight(container.clientHeight)
    updateViewportHeight()

    const resizeObserver = new ResizeObserver(updateViewportHeight)
    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [chatContainerRef])

  // One-shot scroll: when the conversation grows after a send/retry, bring the
  // new question to the top. Requiring actual growth (not just the armed flag)
  // makes this robust against the retry path, which first removes the old
  // thread and then re-adds it, transiently changing the array.
  useEffect(() => {
    const conversationGrew = conversation.length > previousConversationLengthRef.current
    previousConversationLengthRef.current = conversation.length

    if (!shouldScrollOnGrowthRef.current || !conversationGrew) return

    const container = chatContainerRef.current
    const lastThread = lastThreadRef.current
    if (!container || !lastThread) return

    // Scroll the container itself rather than using scrollIntoView, which would
    // also scroll overflow:hidden ancestors and shift the entire layout.
    const offsetFromContainerTop = lastThread.getBoundingClientRect().top - container.getBoundingClientRect().top
    container.scrollTo({
      top: container.scrollTop + offsetFromContainerTop - SCROLL_ANCHOR_TOP_GAP,
      behavior: 'smooth',
    })
    shouldScrollOnGrowthRef.current = false
  }, [conversation.length, chatContainerRef])

  // Handler for sending messages that includes the current tool
  const handleSendMessage = (message: string, attachedDocument?: UploadedAsset, commands?: ParsedCommand[]) => {
    shouldScrollOnGrowthRef.current = true
    onSend(message, attachedDocument, commands)
  }

  // Handler for retrying messages that includes the current tool
  const handleRetry = (question: string, tempId: string) => {
    shouldScrollOnGrowthRef.current = true
    onRetry(question, tempId)
  }

  // Handler for example question clicks
  const handleExampleQuestionClick = (question: string) => {
    handleSendMessage(question)
  }

  return (
    <Flex h='100%' flexDir='column'>
      <Flex
        ref={chatContainerRef}
        flex={1}
        minHeight='0'
        overflowY='scroll'
        overflowX='hidden'
        flexDirection='column'
      >
        <Box px={CHAT_SPACING.horizontal} pt={0} pb={6} h='100%'>
          {conversation.length === 0 && currentAgent ? (
            <AgentWatermark agent={currentAgent} onQuestionClick={handleExampleQuestionClick} />
          ) : (
            conversation.map((thread, index) => {
              const isLastThread = index === conversation.length - 1
              return (
                <Box
                  key={thread.tempId}
                  ref={isLastThread ? lastThreadRef : undefined}
                  minHeight={isLastThread && viewportHeight ? `${viewportHeight}px` : undefined}
                >
                  <ChatBubble
                    thread={thread}
                    onRetry={() => {
                      if (thread.question) {
                        handleRetry(thread.question, thread.tempId)
                      }
                    }}
                    onToggleResponseMinimized={
                      onToggleResponseMinimized
                        ? responseMessageId => onToggleResponseMinimized(thread.tempId, responseMessageId)
                        : undefined
                    }
                    currentPhase={currentPhase}
                  />
                </Box>
              )
            })
          )}
        </Box>
      </Flex>
      <Box
        display='flex'
        flexDirection='column'
        position='sticky'
        bottom='0'
        py={2}
        bg={inputContainerBg}
        px={CHAT_SPACING.horizontal}
      >
        <Box display='flex' justifyContent='center'>
          <Box w='100%' maxW='768px'>
            <Box>
              <QuestionInput
                clearOnSend
                placeholder={t('chat.placeholder')}
                disabled={isInputDisabled}
                onTyping={() => onTypingChange(true)}
                onStopTyping={() => onTypingChange(false)}
                onSend={handleSendMessage}
                hasDocumentsInConversation={hasDocumentsInConversation}
                isStreaming={isStreaming}
                onStop={onStop}
                models={models}
                selectedModelId={selectedModelId}
                onSelectModel={onSelectModel}
                conversationId={conversationId}
              />
            </Box>
          </Box>
        </Box>
        <Box pb={4} />
        <Text fontSize='xs' color={textMutedColor} textAlign='center'>
          {t('chat.disclaimerText')}
        </Text>
      </Box>
    </Flex>
  )
}
